import React, { createElement, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from 'src/lib/utils'
import { currencyFullValue, percentageFormat, priceColor, volume } from '../../core/helpers'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Card, CardContent } from '../ui/card'
import { Skeleton } from '../ui/skeleton'
import Pagination, { paginate, paginateSort } from '../Pagination/Pagination'
import WatchStar from '../Watchlist/WatchStar'
import ListSortHead from './ListSortHead'
import Select from '../Select/Select'

function CoinList({ coins, isFetching, emptyMsg, initialSort = { field: 'marketCap', value: 'h_cap', label: 'Highest Cap', desc: true } }) {

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState(initialSort)
  const [sortPage, setSortPage] = useState({ field: initialSort.field, desc: initialSort.desc })

  const sortedCoins = sortCoins(sort, [...coins])

  const perPage = 50
  const paginatedCoins = paginate(sortedCoins, page, perPage);
  const paginatedCoinsSort = paginateSort(paginatedCoins, sortPage)

  if (!isFetching && !coins.length) {
    return (
      <div className="container mx-auto px-4">
        <div className="py-12 text-center">
          <p className="text-2xl text-walme-text-dim">
            {emptyMsg || 'No results'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4">
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="mb-6">
            <div className="w-full md:w-64">
              <Select
                placeholder="Select"
                value={sort}
                onChange={value => {
                  setSort(value)
                  setSortPage({})
                  setPage(1)
                }}
                isSearchable={false}
                options={[
                  { field: 'marketCap', value: 'h_cap', label: 'Highest Cap' },
                  { field: 'marketCap', value: 'l_cap', label: 'Lowest Cap' },
                  { field: 'totalVolume', value: 'h_volume', label: 'Highest Volume' },
                  { field: 'totalVolume', value: 'l_volume', label: 'Lowest Volume' },
                  { field: 'price', value: 'h_price', label: 'Highest Price' },
                  { field: 'price', value: 'l_price', label: 'Lowest Price' },
                  { field: 'priceChange24h', value: 't_gainers', label: 'Top Gainers' },
                  { field: 'priceChange24h', value: 't_losers', label: 'Top Losers' }
                ]}
              />
            </div>
          </div>

          <div className="rounded-lg border border-walme-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <ListSortHead className="w-16" field="rank" sort={sortPage} setSort={setSortPage}>
                    #
                  </ListSortHead>
                  <ListSortHead field="name" sort={sortPage} setSort={setSortPage} fieldString>
                    Name
                  </ListSortHead>
                  <ListSortHead className="text-right" field="price" sort={sortPage} setSort={setSortPage}>
                    Price
                  </ListSortHead>
                  <ListSortHead className="text-right" field="priceChange24h" sort={sortPage} setSort={setSortPage}>
                    24H
                  </ListSortHead>
                  <ListSortHead className="text-right" field="priceChange7d" sort={sortPage} setSort={setSortPage}>
                    7D
                  </ListSortHead>
                  <ListSortHead className="text-right" field="marketCap" sort={sortPage} setSort={setSortPage}>
                    Market Cap
                  </ListSortHead>
                  <ListSortHead className="text-right" field="totalVolume" sort={sortPage} setSort={setSortPage}>
                    Volume
                  </ListSortHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isFetching
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      </TableRow>
                    ))
                  : paginatedCoinsSort.map(coinMapper)}
              </TableBody>
            </Table>
          </div>

          <div className="mt-6">
            <Pagination totalCount={sortedCoins.length} perPage={perPage} page={page} onClick={setPage} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function coinMapper({ id, name, rank, image, price, marketCap, totalVolume, priceChange24h, priceChange7d }, index) {
  return (
    <TableRow key={index} className="cursor-pointer">
      <TableCell className="py-3">
        <WatchStar size="16" coin={id} />
      </TableCell>
      <TableCell className="font-medium text-walme-text-dim">
        {rank}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <img src={image} alt={name} className="w-6 h-6 rounded-full" />
          {createElement(id ? Link : 'span', {
            to: id ? `/coins/${id}` : undefined,
            className: 'font-semibold text-walme-text hover:text-walme-primary transition-colors'
          }, name)}
        </div>
      </TableCell>
      <TableCell className="text-right font-medium">{currencyFullValue(price)}</TableCell>
      <TableCell className={cn('text-right font-semibold', priceColor(priceChange24h))}>
        {percentageFormat(priceChange24h)}
      </TableCell>
      <TableCell className={cn('text-right font-semibold', priceColor(priceChange7d))}>
        {percentageFormat(priceChange7d)}
      </TableCell>
      <TableCell className="text-right">${volume(marketCap)}</TableCell>
      <TableCell className="text-right">${volume(totalVolume)}</TableCell>
    </TableRow>
  )
}

function sortCoins(filter, list) {
  if (!filter) {
    return list
  }

  const { value, field } = filter
  switch (value) {
    case 'h_cap':
    case 'h_volume':
    case 'h_price':
    case 't_gainers': {
      return list.sort((a, b) => b[field] - a[field])
    }
    case 'l_cap':
    case 'l_volume':
    case 'l_price':
    case 't_losers': {
      return list.sort((a, b) => a[field] - b[field])
    }
    default:
      return list
  }
}

export default CoinList
